/**
 * vbr-repo-migration.js — VBR Repository Migration Section
 *
 * Owns:
 *   - buildDefaultVbrRepoMigrationEntry  (data model factory)
 *   - DOM refs for the VBR repository migration section
 *   - syncVbrRepoMigrationSectionVisibility (exported) — show/hide based on
 *     presence of VBR repository servers in the existing environment (Step 1)
 *   - renderVbrRepoMigrationSection — renders all scope rows
 *   - All delegated DOM events for the rows host and add-migration button
 *
 * Each scope row captures:
 *   - sourceType          — repository type being migrated from:
 *                           'block' | 'standalone' | 'sobr-performance-tier'
 *                           (each row represents exactly one repository — count is implicit)
 *   - targetType          — migration target repository type
 *   - includeHardeningSetup — whether deploying and hardening a new target repository
 *                             is in scope for this engagement (default: true)
 *
 * Repository count and data volume (TB) are derived from Step 1 workload data at
 * calculation time (recalc.js) — they are not stored on the scope object.
 *
 * Exports:
 *   syncVbrRepoMigrationSectionVisibility — called from syncDesignOptionFields in script.js
 *
 * Window bridge written:
 *   window.vbrRepoMigrationActive — boolean; consumed by syncMigrationGroupVisibility in helpers.js
 */

import { sites, vbrRepoMigrationScopes } from './state.js';
import { vbrRepoMigrationSourceTypeOptions } from './workload-defs.js';
import { escapeHtml, formatProtectedTbValue } from './utils.js';
import { repoMigrationTargetOptions, repoMigrationSourceStorageOptions, repoMigrationRepositoryOptions } from './site-scope-picker.js';
import { scheduleRenderEstimate, getExistingEnvironmentSnapshotQuantity } from './recalc.js';
import {
    syncMigrationGroupVisibility,
    createVbrRepoMigrationId, hasRepositoryComponents,
    getRepoMigrationWorkloads, mapVbrRepoWorkloadToSourceType,
    getRepoMigrationWorkloadProtectedTb, getRepoMigrationPickerOptionLabel,
} from './helpers.js';

// ── VBR repository migration data model ───────────────────────────────────────

export function buildDefaultVbrRepoMigrationEntry() {
    const sourceOpts = getVbrStep1RepositorySourceOptions();
    const isPerRepo = sourceOpts.length > 0 && sourceOpts[0].rowId != null;
    let first;
    if (isPerRepo) {
        // Auto-select the first repo not already claimed by another scope row
        const usedRowIds = new Set(vbrRepoMigrationScopes
            .filter((s) => s.sourceRowId != null)
            .map((s) => String(s.sourceRowId)));
        first = sourceOpts.find((o) => !usedRowIds.has(o.value));
    } else {
        first = sourceOpts[0];
    }
    return {
        id:                    createVbrRepoMigrationId(),
        // sourceRowId points to the specific Step 1 repo workload (null for legacy static-type scopes)
        sourceRowId:           first?.rowId ?? null,
        sourceType:            first?.sourceType ?? first?.value ?? 'block',
        targetType:            'block-performance-tier',
        includeHardeningSetup: true,
        vaultAnalyzerClear:    true,
    };
}

// ── Source type helpers ───────────────────────────────────────────────────────

/** Returns the display label for a source type value, or the value itself if unknown. */
function sourceTypeLabel(value) {
    return vbrRepoMigrationSourceTypeOptions.find((o) => o.value === value)?.label ?? value;
}

/**
 * Builds source options for the migration dropdown from the individual VBR repository
 * components configured in Step 1 (workloads with isRepositoryComponent=true).
 *
 * Each repo workload gets its own entry, keyed by rowId, so multiple repos of the
 * same type can each have their own migration scope row in Step 2.
 *
 * Label = customLabel if the user renamed the chip; otherwise a descriptive fallback
 * using the source storage type (e.g. "VBR Repository — AWS S3").
 *
 * Each option also carries:
 *   rowId      — the workload's rowId (for per-instance TB lookup in recalc.js)
 *   sourceType — the mapped category ('sobr-performance-tier' | 'block' | 'standalone')
 *                used by the effort formula
 *
 * Falls back to the full static vbrRepoMigrationSourceTypeOptions list when no
 * individually-configured repository components are present.
 */
function getVbrStep1RepositorySourceOptions() {
    const result = [];

    sites.forEach((site) => {
        const siteName = site.name || `Site ${site.id}`;
        getRepoMigrationWorkloads(site)
            .filter((w) => w.isRepositoryComponent)
            .forEach((w) => {
                const storageLabel = w.vdcVaultSourceStorageType
                    ? getRepoMigrationPickerOptionLabel(repoMigrationSourceStorageOptions, w.vdcVaultSourceStorageType)
                    : '';
                // Primary name: use customLabel if the user renamed the chip, otherwise fall
                // back to 'VBR Repository' — matching the hardcoded sourceLabel the chip
                // itself uses (recalc.js: isRepositoryComponent ? 'VBR Repository' : rawSourceLabel).
                // Storage type is appended in parentheses only when no custom name is set —
                // it disambiguates repos that share the same default label.
                const repoTypeLabel = w.vdcVaultRepositoryType
                    ? getRepoMigrationPickerOptionLabel(repoMigrationRepositoryOptions, w.vdcVaultRepositoryType)
                    : '';
                const nameLabel = w.customLabel || 'VBR Repository';
                // When no custom name is set, append storage type + repo type as disambiguation context.
                const contextParts = !w.customLabel
                    ? [storageLabel, repoTypeLabel].filter(Boolean)
                    : [];
                const storageContext = contextParts.length ? ` (${contextParts.join(' · ')})` : '';
                const tb = getRepoMigrationWorkloadProtectedTb(w);
                const tbPart = tb > 0 ? ` · ${formatProtectedTbValue(tb)} TB` : '';
                const label = `${nameLabel}${storageContext}${tbPart} · ${siteName}`;
                result.push({
                    value:      String(w.rowId),
                    label,
                    rowId:      w.rowId,
                    sourceType: mapVbrRepoWorkloadToSourceType(w),
                });
            });
    });

    // No individually-configured repos in Step 1 — fall back to the full static type list.
    return result.length > 0 ? result : vbrRepoMigrationSourceTypeOptions;
}

// ── DOM refs ──────────────────────────────────────────────────────────────────

const vbrRepoMigrationRowsHost = document.getElementById('vbr-repo-migration-rows-host');

// Window bridge — read by syncMigrationGroupVisibility in helpers.js
window.vbrRepoMigrationActive = false;

// ── Visibility helpers ────────────────────────────────────────────────────────

/** Returns true when at least one site has one or more VBR backup repository servers
 *  recorded in the existing environment component counts. */
function hasVbrRepositories() {
    return sites.some((site) =>
        getExistingEnvironmentSnapshotQuantity([site], 'repositoryServers') > 0
    );
}

/** Returns true when at least one site has a named VBR server instance. */
function hasVbrServer() {
    return sites.some((site) =>
        Array.isArray(site.veeamServers) && site.veeamServers.some((s) => s.type === 'vbr')
    );
}

/**
 * Shows/hides the Add VBR Repository Migration button.
 * In per-repo mode: hides the button once every Step 1 repo has a scope row.
 * In static-fallback mode: always shows (multiple rows of same type are valid).
 */
function syncVbrAddButtonAvailability() {
    const addBtn = document.getElementById('add-vbr-repo-migration-btn');
    if (!addBtn) return;
    const sectionVisible = hasVbrRepositories() || hasVbrServer() || hasRepositoryComponents();
    if (!sectionVisible) { addBtn.hidden = true; return; }
    const sourceOpts = getVbrStep1RepositorySourceOptions();
    const isPerRepo  = sourceOpts.length > 0 && sourceOpts[0].rowId != null;
    if (!isPerRepo)  { addBtn.hidden = false; return; }
    const usedRowIds = new Set(vbrRepoMigrationScopes
        .filter((s) => s.sourceRowId != null)
        .map((s) => String(s.sourceRowId)));
    addBtn.hidden = sourceOpts.every((o) => usedRowIds.has(o.value));
}

// ── Exported visibility sync ──────────────────────────────────────────────────

/** Show/hide the VBR add button and update the active flag.
 *  Requires VBR repository servers, a named VBR server, or repository
 *  components added to the canvas in Step 1. */
export function syncVbrRepoMigrationSectionVisibility() {
    const shouldShow = hasVbrRepositories() || hasVbrServer() || hasRepositoryComponents();
    window.vbrRepoMigrationActive = shouldShow;
    renderVbrRepoMigrationSection(); // also calls syncVbrAddButtonAvailability
    syncMigrationGroupVisibility();
}

// ── Section render ────────────────────────────────────────────────────────────

/** Syncs the shared empty-state message and table-wrap visibility.
 *  Called after rendering — checks both tbodies so each module stays decoupled. */
function syncRepoMigrationEmptyState() {
    const hasVbrRows   = vbrRepoMigrationRowsHost  && vbrRepoMigrationRowsHost.querySelector('tr[data-vbrrm-scope]')   !== null;
    const hasVb365Rows = document.querySelector('#vb365-repo-migration-rows-host tr[data-vb365rm-scope]') !== null;
    const hasAnyRows   = hasVbrRows || hasVb365Rows;
    const emptyEl  = document.getElementById('repo-migration-empty-state');
    const tableWrap = document.getElementById('repo-migration-table-wrap');
    if (emptyEl)   emptyEl.hidden   = hasAnyRows;
    if (tableWrap) tableWrap.hidden = !hasAnyRows;
}

/** Renders all VBR repository migration scope rows into the shared table tbody. */
function renderVbrRepoMigrationSection() {
    if (!vbrRepoMigrationRowsHost) return;

    if (vbrRepoMigrationScopes.length === 0) {
        vbrRepoMigrationRowsHost.innerHTML = '';
        syncRepoMigrationEmptyState();
        syncVbrAddButtonAvailability();
        return;
    }

    const svgClose = `<svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;

    const step1SourceOpts = getVbrStep1RepositorySourceOptions();
    // When Step 1 has individually-configured repos, each row's dropdown should only
    // show repos not already claimed by another row (plus the row's own current selection).
    const isPerRepo = step1SourceOpts.length > 0 && step1SourceOpts[0].rowId != null;

    const rowsHtml = vbrRepoMigrationScopes.map((scope) => {
        // ── Source type select ───────────────────────────────────────────────
        // Determine the option value that should be selected for this scope.
        // New scopes: sourceRowId is a number → match by String(sourceRowId).
        // Legacy scopes: sourceRowId is null → match by sourceType category string.
        const selectedVal = scope.sourceRowId != null
            ? String(scope.sourceRowId)
            : (scope.sourceType ?? '');

        // Build the filtered option list for this row:
        // - In per-repo mode: exclude rowIds claimed by OTHER rows (keep own selection).
        // - In static-fallback mode: show all options (multiple rows of same type are valid).
        const usedByOthers = isPerRepo
            ? new Set(vbrRepoMigrationScopes
                .filter((s) => s.id !== scope.id && s.sourceRowId != null)
                .map((s) => String(s.sourceRowId)))
            : new Set();
        let scopeSourceOpts = isPerRepo
            ? step1SourceOpts.filter((o) => !usedByOthers.has(o.value) || o.value === selectedVal)
            : step1SourceOpts;

        // If the saved selection is no longer in the options (e.g. the Step 1 repo was
        // removed), append a fallback entry so the selection is never silently dropped.
        if (selectedVal && !scopeSourceOpts.some((o) => o.value === selectedVal)) {
            const staticFallback = vbrRepoMigrationSourceTypeOptions.find((o) => o.value === scope.sourceType);
            const fallback = staticFallback ?? { value: selectedVal, label: '(removed)' };
            scopeSourceOpts = [...scopeSourceOpts, fallback];
        }
        const sourceOpts = scopeSourceOpts.map((opt) => {
            const sel = selectedVal === opt.value ? ' selected' : '';
            return `<option value="${escapeHtml(opt.value)}"${sel}>${escapeHtml(opt.label)}</option>`;
        }).join('');

        // ── Target type select ───────────────────────────────────────────────
        // Skip the leading placeholder option (value='') — every row must have a target.
        const targetOpts = repoMigrationTargetOptions
            .filter((opt) => opt.value !== '')
            .map((opt) => {
                const sel = scope.targetType === opt.value ? ' selected' : '';
                return `<option value="${escapeHtml(opt.value)}"${sel}>${escapeHtml(opt.label)}</option>`;
            }).join('');

        // ── Options cell ─────────────────────────────────────────────────────
        // VDC Vault targets are pre-hardened BaaS — show vault analyzer check instead.
        const isVaultTarget    = scope.targetType === 'vdc-vault-aws' || scope.targetType === 'vdc-vault-azure';
        const hardeningChecked = scope.includeHardeningSetup ? ' checked' : '';
        const vaultAnalyzerChecked = scope.vaultAnalyzerClear !== false ? ' checked' : '';

        return `<tr class="hvm-row" data-vbrrm-scope="${scope.id}">
            <td class="hvm-td hvm-td--type"><span class="hvm-badge hvm-badge--vbr">VBR</span></td>
            <td class="hvm-td hvm-td--from">
                <label class="hvm-field-label">Source type</label>
                <select class="hvm-select" data-vbrrm-source-select="${scope.id}" aria-label="Source repository type">
                    ${sourceOpts}
                </select>
            </td>
            <td class="hvm-td hvm-td--to">
                <label class="hvm-field-label">Target</label>
                <select class="hvm-select" data-vbrrm-target-select="${scope.id}" aria-label="Migration target">
                    ${targetOpts}
                </select>
            </td>
            <td class="hvm-td hvm-td--opts">
                ${isVaultTarget ? `<div class="hvm-checkbox-row">
                    <label class="hvm-checkbox-label">
                        <input type="checkbox" class="hvm-checkbox"
                               data-vbrrm-vault-analyzer="${scope.id}"${vaultAnalyzerChecked}>
                        <span>Vault analyzer tool ran and no blockers found</span>
                    </label>
                </div>` : `<div class="hvm-checkbox-row">
                    <label class="hvm-checkbox-label">
                        <input type="checkbox" class="hvm-checkbox"
                               data-vbrrm-hardening="${scope.id}"${hardeningChecked}
                               aria-label="Target repository provisioning and hardening is in scope for this engagement">
                        <span>Deploy and harden new target repository</span>
                    </label>
                </div>`}
            </td>
            <td class="hvm-td hvm-td--remove">
                <button type="button" class="hvm-remove-btn" data-vbrrm-remove="${scope.id}" aria-label="Remove migration scope">${svgClose}</button>
            </td>
        </tr>`;
    }).join('');

    vbrRepoMigrationRowsHost.innerHTML = rowsHtml;
    syncRepoMigrationEmptyState();
    syncVbrAddButtonAvailability();
}

// ── Section events ────────────────────────────────────────────────────────────

// Add migration scope
document.getElementById('add-vbr-repo-migration-btn')?.addEventListener('click', () => {
    vbrRepoMigrationScopes.push(buildDefaultVbrRepoMigrationEntry());
    renderVbrRepoMigrationSection();
    scheduleRenderEstimate();
});

// Delegated click — remove button
document.getElementById('vbr-repo-migration-rows-host')?.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-vbrrm-remove]');
    if (removeBtn) {
        const scopeId = parseInt(removeBtn.dataset.vbrrmRemove, 10);
        const idx = vbrRepoMigrationScopes.findIndex((s) => s.id === scopeId);
        if (idx !== -1) {
            vbrRepoMigrationScopes.splice(idx, 1);
            renderVbrRepoMigrationSection();
            scheduleRenderEstimate();
        }
    }
});

// Delegated change — source type select, target type select, and hardening checkbox
document.getElementById('vbr-repo-migration-rows-host')?.addEventListener('change', (e) => {
    const sourceSelect = e.target.closest('[data-vbrrm-source-select]');
    if (sourceSelect) {
        const scopeId = parseInt(sourceSelect.dataset.vbrrmSourceSelect, 10);
        const scope = vbrRepoMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            const rawValue = sourceSelect.value;
            const rowId = parseInt(rawValue, 10);
            if (!isNaN(rowId)) {
                // Individual repo selected — store rowId and derive the sourceType category
                scope.sourceRowId = rowId;
                let derivedSourceType = 'standalone';
                sites.some((site) => {
                    const w = getRepoMigrationWorkloads(site).find((wl) => wl.rowId === rowId);
                    if (w) { derivedSourceType = mapVbrRepoWorkloadToSourceType(w); return true; }
                    return false;
                });
                scope.sourceType = derivedSourceType;
            } else {
                // Static fallback category selected (no Step 1 repo components present)
                scope.sourceRowId = null;
                scope.sourceType  = rawValue;
            }
            // Re-render so other rows' dropdowns reflect the newly claimed/released repo
            renderVbrRepoMigrationSection();
            scheduleRenderEstimate();
        }
        return;
    }

    const targetSelect = e.target.closest('[data-vbrrm-target-select]');
    if (targetSelect) {
        const scopeId = parseInt(targetSelect.dataset.vbrrmTargetSelect, 10);
        const scope = vbrRepoMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.targetType = targetSelect.value;
            // Re-render the section so the hardening checkbox shows/hides based on
            // whether the new target is a vault type (vault targets are pre-hardened BaaS).
            renderVbrRepoMigrationSection();
            scheduleRenderEstimate();
        }
        return;
    }

    const hardeningCheck = e.target.closest('[data-vbrrm-hardening]');
    if (hardeningCheck) {
        const scopeId = parseInt(hardeningCheck.dataset.vbrrmHardening, 10);
        const scope = vbrRepoMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.includeHardeningSetup = hardeningCheck.checked;
            scheduleRenderEstimate();
        }
        return;
    }

    const vaultAnalyzerCheck = e.target.closest('[data-vbrrm-vault-analyzer]');
    if (vaultAnalyzerCheck) {
        const scopeId = parseInt(vaultAnalyzerCheck.dataset.vbrrmVaultAnalyzer, 10);
        const scope = vbrRepoMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.vaultAnalyzerClear = vaultAnalyzerCheck.checked;
            scheduleRenderEstimate();
        }
        return;
    }
});


// ── End VBR Repository Migration Section ──────────────────────────────────────
